import { useEffect, useRef, useState } from 'react';

const formatTimestamp = (ms: number) => {
  const h = String(Math.floor(ms / 3600000)).padStart(2, '0');
  const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
  const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
  const msms = String(ms % 1000).padStart(3, '0');
  return `${h}:${m}:${s}.${msms}`;
};

const parseTimestamp = (input: string): number | null => {
  const match = input.match(/^(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/);
  if (!match) return null;
  const [, h, m, s, ms] = match;
  return (
    parseInt(h) * 3600000 +
    parseInt(m) * 60000 +
    parseInt(s) * 1000 +
    parseInt(ms)
  );
};

type Props = {
  value: number;
  onChange: (newValue: number) => void;
  fieldType: 'start' | 'stop';
  /**
   * Read-only display — used by the caption translation pane in split
   * view where timings belong to the source segment. The input still
   * renders (so alignment / focus outlines stay consistent with the
   * source pane) but rejects keys and mutations.
   */
  disabled?: boolean;
};

// Digit positions in "HH:MM:SS.mmm" (indices 0-11)
// H H : M M : S S . m  m  m
// 0 1   3 4   6 7   9 10 11
const DIGIT_POSITIONS = [0, 1, 3, 4, 6, 7, 9, 10, 11];

const TimestampInput = ({ value, onChange, disabled }: Props) => {
  const [inputValue, setInputValue] = useState(formatTimestamp(value));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(formatTimestamp(value));
    }
  }, [value, isFocused]);

  // #2291: push every valid edit to the parent immediately, not just on
  // blur. Save triggers (Ctrl+S, header Save, autosave) run while the
  // input can still be focused, and the pre-fix "wait for blur" path
  // dropped the typed value whenever a save fired before focus moved
  // off the input.
  const commit = (next: string) => {
    setInputValue(next);
    const parsed = parseTimestamp(next);
    if (parsed !== null) onChange(parsed);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseTimestamp(inputValue);
    if (parsed !== null) {
      onChange(parsed);
    } else {
      setInputValue(formatTimestamp(value));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const mod = e.ctrlKey || e.metaKey;

    // Let undo/redo bubble up to the editor untouched
    if (
      mod &&
      (e.key === 'z' || e.key === 'Z' || e.key === 'y' || e.key === 'Y')
    ) {
      return;
    }

    // Let navigation keys pass through without consuming them
    if (
      [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Tab',
        'Enter'
      ].includes(e.key)
    ) {
      e.stopPropagation();
      return;
    }

    // Block everything else at the DOM level — we drive all edits manually
    e.preventDefault();
    e.stopPropagation();

    const input = inputRef.current;
    if (!input) return;

    const sel = input.selectionStart ?? 0;

    if (e.key.match(/[0-9]/)) {
      // Replace the digit at or after the cursor, then advance
      const idx = DIGIT_POSITIONS.findIndex(p => p >= sel);
      if (idx === -1) return;
      const pos = DIGIT_POSITIONS[idx];

      const updated =
        inputValue.slice(0, pos) + e.key + inputValue.slice(pos + 1);
      commit(updated);

      const nextPos =
        idx + 1 < DIGIT_POSITIONS.length ? DIGIT_POSITIONS[idx + 1] : pos + 1;
      setTimeout(() => input.setSelectionRange(nextPos, nextPos), 0);
    } else if (e.key === 'Backspace') {
      // Zero the digit before the cursor and move back to it
      // (never shrink the string — that's what caused the original bug)
      let prevIdx = -1;
      for (let i = 0; i < DIGIT_POSITIONS.length; i++) {
        if (DIGIT_POSITIONS[i] < sel) prevIdx = i;
      }
      if (prevIdx === -1) return;
      const pos = DIGIT_POSITIONS[prevIdx];

      const updated =
        inputValue.slice(0, pos) + '0' + inputValue.slice(pos + 1);
      commit(updated);
      setTimeout(() => input.setSelectionRange(pos, pos), 0);
    } else if (e.key === 'Delete') {
      // Zero the digit at or after the cursor and advance
      const idx = DIGIT_POSITIONS.findIndex(p => p >= sel);
      if (idx === -1) return;
      const pos = DIGIT_POSITIONS[idx];

      const updated =
        inputValue.slice(0, pos) + '0' + inputValue.slice(pos + 1);
      commit(updated);

      const nextPos =
        idx + 1 < DIGIT_POSITIONS.length ? DIGIT_POSITIONS[idx + 1] : pos + 1;
      setTimeout(() => input.setSelectionRange(nextPos, nextPos), 0);
    }
    // All other keys (letters, symbols, paste shortcuts, etc.) are already
    // blocked by the e.preventDefault() above.
  };

  return (
    <div
      className="flex items-center justify-center"
      onClick={e => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onChange={() => {}}
        onKeyDown={disabled ? e => e.preventDefault() : handleKeyDown}
        readOnly={disabled}
        aria-readonly={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        className="bg-transparent border-none pl-1 w-full text-[11px] font-mono tabular-nums focus:outline-none text-gray-500 dark:text-zinc-400 text-right"
        spellCheck={false}
        autoComplete="off"
      />
    </div>
  );
};

export default TimestampInput;
